import { useRef, useState } from "react";
import { toast } from "sonner";
import { errMsg, useApi } from "../../lib/api";

const MAX_MS = 60 * 60 * 1000;

// Records the stage videos (drawn to a canvas) plus the master mix and the host's own mic.
export function useRecorder({ spaceId, engine, localMic, title }) {
  const api = useApi();
  const [startedAt, setStartedAt] = useState(null);
  const [uploading, setUploading] = useState(false);
  const r = useRef({});

  const start = () => {
    if (!window.MediaRecorder) return toast.error("This browser can't record");
    const canvas = document.createElement("canvas");
    canvas.width = 1280;
    canvas.height = 720;
    const g = canvas.getContext("2d");
    const draw = () => {
      g.fillStyle = "#08080a";
      g.fillRect(0, 0, 1280, 720);
      const vids = [...document.querySelectorAll("[data-stage-video] video")].filter((v) => v.videoWidth);
      const cols = Math.ceil(Math.sqrt(Math.max(1, vids.length)));
      const rows = Math.ceil(Math.max(1, vids.length) / cols);
      const w = 1280 / cols, h = 720 / rows;
      vids.forEach((v, i) => {
        const s = Math.min(w / v.videoWidth, h / v.videoHeight);
        const dw = v.videoWidth * s, dh = v.videoHeight * s;
        g.drawImage(v, (i % cols) * w + (w - dw) / 2, Math.floor(i / cols) * h + (h - dh) / 2, dw, dh);
      });
      if (!vids.length) {
        g.fillStyle = "#facc15";
        g.font = "bold 56px Outfit, sans-serif";
        g.fillText(title, 64, 380);
      }
      g.fillStyle = "#ec4899";
      g.font = "bold 20px monospace";
      g.fillText("● REC  BANDCAMP", 24, 40);
    };
    const timer = setInterval(draw, 1000 / 30);
    let micNode = null;
    if (localMic) {
      micNode = engine.ctx.createMediaStreamSource(new MediaStream([localMic]));
      micNode.connect(engine.recordDest);
    }
    const stream = new MediaStream([...canvas.captureStream(30).getVideoTracks(), ...engine.recordDest.stream.getAudioTracks()]);
    const mimeType = ["video/webm;codecs=vp8,opus", "video/webm"].find((t) => MediaRecorder.isTypeSupported(t));
    const mr = new MediaRecorder(stream, { mimeType, videoBitsPerSecond: 1_200_000 });
    const chunks = [];
    const t0 = Date.now();
    mr.ondataavailable = (e) => e.data.size && chunks.push(e.data);
    mr.onstop = async () => {
      clearInterval(timer);
      clearTimeout(r.current.cap);
      micNode?.disconnect();
      setStartedAt(null);
      const fd = new FormData();
      fd.append("file", new Blob(chunks, { type: "video/webm" }), "recording.webm");
      fd.append("title", `${title} — ${new Date(t0).toLocaleString()}`);
      fd.append("duration", String((Date.now() - t0) / 1000));
      setUploading(true);
      try {
        await api.post(`/spaces/${spaceId}/recordings`, fd);
        toast.success("Recording saved to the music library");
      } catch (e) { toast.error(errMsg(e, "Recording upload failed")); }
      setUploading(false);
    };
    mr.start(1000);
    r.current = { mr, cap: setTimeout(() => mr.state !== "inactive" && mr.stop(), MAX_MS) };
    setStartedAt(t0);
  };

  const stop = () => r.current.mr?.state === "recording" && r.current.mr.stop();
  return { recording: !!startedAt, startedAt, uploading, start, stop };
}
